import { Component, ViewChild } from '@angular/core';
import { OrganisationUnitService } from '../../../services/organisation-unit.service';
import { forkJoin } from 'rxjs';
import { OrganisationUnitModel } from '../../../models/organisation-unit/organisation-unit.model';
import { map, shareReplay } from 'rxjs/operators';
import { TrainingGroupModel } from '../../../models/training-group.model';
import { RoleModel } from '../../../models/person/role.model';
import { TrainingGroupService } from '../../../services/training-group.service';
import { RoleService } from '../../../services/role.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../../../services/loading.service';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { DxFormComponent } from 'devextreme-angular/ui/form';
import { LearningPathModel } from 'src/app/models/learning-paths/learning-path.model';
import { LearningPathService } from 'src/app/services/learning-path.service';
import { RegistrationLinkCreationAndUpdateModel } from 'src/app/models/registration-link/registration-link-creation-and-update.model';
import { TranslateService } from '@ngx-translate/core';
import { RegistrationLinkService } from 'src/app/services/registration-link.service';
import { environment } from 'src/environments/environment';
import { ToastrService } from 'ngx-toastr';
import { Clipboard } from '@angular/cdk/clipboard';
import { TableColumn } from 'src/app/models/table-column.model';
import { RegistrationLinkFullModel } from 'src/app/models/registration-link/registration-link-full.model';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { RegistrationFieldConfigComponent } from 'src/app/components/registration-field-config/registration-field-config.component';
import { RegistrationFieldConfigService } from 'src/app/services/registration-field-config.service';
import { RegistrationLinkQuestionsComponent } from './registration-link-questions/registration-link-questions.component';
import { TenantContextService } from 'src/app/services/tenant-context.service';

@Component({
    selector: 'app-registration-info',
    templateUrl: './registration-link-info.component.html',
    styleUrl: './registration-link-info.component.scss',
    standalone: false
})
export class RegistrationLinkInfoComponent {
  @ViewChild('formBasicData', { static: true }) 
  formBasicData!: DxFormComponent;

  @ViewChild('formAutomaticAllocationsData', { static: true }) 
  formAutomaticAllocationsData!: DxFormComponent;
  
  @ViewChild('registrationFieldConfigs')
  registrationFieldConfigsForm!: RegistrationFieldConfigComponent;

  @ViewChild('registrationQuestions')
  registrationQuestions!: RegistrationLinkQuestionsComponent;

  editingLinkId!: string;

  locations!: OrganisationUnitModel[];
  trainingGroups!: TrainingGroupModel[];
  learningPaths!: LearningPathModel[];
  roles!: RoleModel[];
  isBrokerPoolTenant: boolean = false;
  registrationLinkFull?: RegistrationLinkFullModel;
  registrationLinkForm: RegistrationLinkCreationAndUpdateModel = {
    name: '',
    title: '',
    description: null,
    allowAddingEmployees: false,
    createLocationAutomatically: false,
    parentLocationId: null,
    locationId: '',
    roleId: '',
    trainingGroupIds: [],
    learningPathIds: [],
    questions: [],
    fieldConfigs: [],
  };

  rolesOptions!: DxSelectBoxTypes.Properties;

  personsColumns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.EMAIL'),
      dataField: 'email',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.LAST-NAME'),
      dataField: 'lastName',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.FIRST-NAME'),
      dataField: 'firstName',
    },
  ];
  
  constructor(
    private registrationLinkService: RegistrationLinkService,
    private organisationUnitService: OrganisationUnitService,
    private trainingGroupService: TrainingGroupService,
    private learningPathService: LearningPathService,
    private roleService: RoleService,
    private route: ActivatedRoute,
    private router: Router,
    private loadingService: LoadingService,
    private translateService: TranslateService,
    private clipboard: Clipboard,
    private toastrService: ToastrService,
    private pageStateService: PageStateService,
    private registrationFieldConfigService: RegistrationFieldConfigService,
    private tenantContextService: TenantContextService
  ) {
    const formData$ = this.loadingService.load(
      forkJoin({
        organizationUnits: this.organisationUnitService.getAllOrganisationUnits(),
        trainingGroups: this.trainingGroupService.getAllTrainingGroups(),
        learningPaths: this.learningPathService.getAllLearningPaths().pipe(
          map((learningPaths) => learningPaths.map((learningPath) => ({ ...learningPath, selected: false })))
        ),
        roles: this.roleService.getAllRoles(),
      })
    ).pipe(shareReplay(1));

    this.editingLinkId = this.route.snapshot.params['id'];

    this.isBrokerPoolTenant =
      this.tenantContextService.isBrokerPoolTenant()

    formData$.subscribe(({ 
      organizationUnits, trainingGroups, learningPaths, roles,
     }) => {
      this.locations = organizationUnits;
      this.trainingGroups = trainingGroups;
      this.learningPaths = learningPaths;
      this.roles = roles;

      this.roles.forEach((role) => {
        if (role.name === 'Employee') {
          role.name = this.translateService.instant('COMMON.EMPLOYEE');
        }
        if (role.name === 'Broker') {
          role.name = this.translateService.instant('COMMON.BROKER');
        }
      });

      if (this.editingLinkId) {
        this.loadingService
          .load(this.registrationLinkService.getRegistrationLinkFull(this.editingLinkId))
          .subscribe((registrationLinkFull) => {
            this.registrationLinkFull = registrationLinkFull;
            this.patchForm(registrationLinkFull);
          });
      } else {
        this.loadingService
          .load(
            this.registrationFieldConfigService.getAllRegistrationFieldConfigs()
          ).subscribe(fieldConfigs => {
            // company name is always visible, required, and locked.
            this.registrationLinkForm.fieldConfigs = fieldConfigs.map(config =>
              config.key === 'companyName'
                ? { ...config, visible: true, required: true, isLocked: true }
                : config
            );
          })
      }

      this.setDropdownOptions();
    });
  }

  navigateBack(): void {
    this.pageStateService.setFlag(
      PageStateFlags.RestoreRegistrationLinksOverview
    );

    this.router.navigate(['registration-links']);
  }

  onFormSubmit(): void {
    const isBasicFormValid =
      this.formBasicData.instance.validate().isValid;

    const isAutomaticAllocationsFormValid =
      this.formAutomaticAllocationsData.instance.validate().isValid;

    const isRegistrationQuestionsValid =
      !this.registrationQuestions || 
      this.registrationQuestions.validate();

    if (
      !isRegistrationQuestionsValid &&
      this.registrationQuestions
    ) {
      this.registrationQuestions.scrollIntoView();
    }

    if (
      !isBasicFormValid ||
      !isAutomaticAllocationsFormValid ||
      !isRegistrationQuestionsValid
    ) {
      this.toastrService.error(
        this.translateService.instant('VALIDATIONS.FORM-INVALID')
      );
      return;
    }

    if (this.registrationFieldConfigsForm) {
      this.registrationLinkForm.fieldConfigs =
        this.registrationFieldConfigsForm.fields;
    }

    if (!this.editingLinkId) {
      this.loadingService.load(
        this.registrationLinkService.addRegistrationLink(
          this.registrationLinkForm
        )
      ).subscribe({
        next: (registrationLink) => {
          this.toastrService.success(
            this.translateService.instant(
              'REGISTRATION-LINKS.FORM.REGISTRATION-LINK-CREATED'
            )
          );

          this.router.navigate([
            'registration-links',
            registrationLink.registrationLink.id
          ]);
        },
        error: () => {
          this.toastrService.error(
            this.translateService.instant('COMMON.AN-ERROR-HAS-OCCURRED')
          );
        }
      });
    } else {
      this.loadingService.load(
        this.registrationLinkService.updateRegistrationLink(
          this.editingLinkId,
          this.registrationLinkForm
        )
      ).subscribe({
        next: () => {
          this.toastrService.success(
            this.translateService.instant(
              'REGISTRATION-LINKS.FORM.REGISTRATION-LINK-UPDATED'
            )
          );
        },
        error: () => {
          this.toastrService.error(
            this.translateService.instant('COMMON.AN-ERROR-HAS-OCCURRED')
          );
        }
      });
    }
  }

  onLocationSelected(e: any) {
    if (e.node.key) {
      this.registrationLinkForm.locationId = e.node.key;
    }
  }

  onTrainingGroupSelected(e: any) {
    if (e.node.selected) {
      this.registrationLinkForm.trainingGroupIds = [
        ...this.registrationLinkForm.trainingGroupIds,
        e.node.key,
      ];
    } else {
      this.registrationLinkForm.trainingGroupIds =
        this.registrationLinkForm.trainingGroupIds.filter((id) => id !== e.node.key);
    }
  }

  onLearningPathSelected(e: any) {
    if (e.node.selected) {
      this.registrationLinkForm.learningPathIds = [
        ...this.registrationLinkForm.learningPathIds,
        e.node.key,
      ];
    } else {
      this.registrationLinkForm.learningPathIds =
        this.registrationLinkForm.learningPathIds.filter((id) => id !== e.node.key);
    }
  }

  copyRegistrationLink() {
    const url =
      window.location.origin +
      environment.registerBaseUrl +
      '/' +
      this.registrationLinkFull!.registrationLink!.key;

    if (this.clipboard.copy(url)) {
      this.toastrService.success(this.translateService.instant("COPY-LINK.COPY-SUCCESS"));
    } else {
      this.toastrService.warning(this.translateService.instant("COPY-LINK.COPY-FAILURE"));
    }
  }

  deleteRegistrationLink(){
    const result = this.registrationLinkService.deleteRegistrationLink(this.editingLinkId);
    this.loadingService.load(result).subscribe({
      next: () => {
        this.toastrService.success(
          this.translateService.instant('REGISTRATION-LINKS.FORM.REGISTRATION-LINK-DELETED')
        );
        this.router.navigate(['registration-links']);
      },
      error: () => {
        this.toastrService.error(
          this.translateService.instant('COMMON.AN-ERROR-HAS-OCCURRED')
        );
      }
    });
  }

  patchForm(registrationLinkFull: RegistrationLinkFullModel) {
    this.registrationLinkForm.name = registrationLinkFull.registrationLink.name;
    this.registrationLinkForm.title = registrationLinkFull.registrationLink.title;
    this.registrationLinkForm.description = registrationLinkFull.registrationLink.description;
    this.registrationLinkForm.allowAddingEmployees = registrationLinkFull.registrationLink.allowAddingEmployees;
    this.registrationLinkForm.createLocationAutomatically = registrationLinkFull.registrationLink.createLocationAutomatically;
    this.registrationLinkForm.parentLocationId = registrationLinkFull.registrationLink.parentLocationId;
    this.registrationLinkForm.locationId = registrationLinkFull.location.id;
    this.registrationLinkForm.roleId = registrationLinkFull.role.id;
    this.registrationLinkForm.trainingGroupIds = registrationLinkFull.trainingGroups.map((tg) => tg.id);
    this.registrationLinkForm.learningPathIds = registrationLinkFull.learningPaths.map((lp) => lp.id);
    this.registrationLinkForm.questions = registrationLinkFull.questions;
    this.registrationLinkForm.fieldConfigs = registrationLinkFull.fieldConfigs;

    this.setTreeViewValues();
  }

  setTreeViewValues() {
    this.trainingGroups.forEach((trainingGroup) => {
      if (this.registrationLinkForm.trainingGroupIds.indexOf(trainingGroup.id) !== -1) {
        trainingGroup.selected = true;
      }
    });

    this.learningPaths.forEach((learningPath) => {
      if (this.registrationLinkForm.learningPathIds.indexOf(learningPath.id) !== -1) {
        learningPath.selected = true;
      }
    });
  }

  setDropdownOptions() {
    const defaultSelectOptions = {
      showClearButton: true,
      stylingMode: 'outlined',
      displayExpr: 'name',
      valueExpr: 'id',
    } as DxSelectBoxTypes.Properties;

    this.rolesOptions = {
      ...defaultSelectOptions,
      dataSource: this.roles,
      placeholder: '',
    };
  }
}
